import { ConnectedPosition, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, HostListener, Input, TemplateRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { BaseViewComponent, PopoverPosition } from '@sb/library/base';

@Component({
    selector: 'lib-popover',
    templateUrl: './popover.component.html',
    styleUrl: './popover.component.scss',
})
export class PopoverComponent extends BaseViewComponent {
    @Input() position?: PopoverPosition;
    @Input() target: HTMLElement | null = null;
    @Input() maxWidth: number | null = null;

    private _overlayRef?: OverlayRef;
    private _positionChangeSubscription?: Subscription;
    activePosition = PopoverPosition.Bottom;
    arrowStyles: Record<string, string> = {};

    constructor(
        private _Overlay: Overlay,
        private _PositionBuilder: OverlayPositionBuilder,
    ) {
        super();
    }

    Toggle(event: MouseEvent | TouchEvent) {
        event.stopPropagation();

        requestAnimationFrame(() => {
            if (this._overlayRef && this._overlayRef.hasAttached()) {
                this.Close();
            }

            const position = this.GetPositions();
            const positionStrategy = this._PositionBuilder
                .flexibleConnectedTo(this.target!)
                .withFlexibleDimensions(true) // allows overlay to resize to fit viewport
                .withPush(true) // pushes overlay inside viewport if clipped
                .withPositions(position);
            this._overlayRef = this._Overlay.create({
                positionStrategy,
                hasBackdrop: true,
                scrollStrategy: this._Overlay.scrollStrategies.reposition(),
                backdropClass: 'transparent'
            });
            this._positionChangeSubscription = positionStrategy.positionChanges.subscribe(({ connectionPair }) => {
                this.activePosition = this.GetPopoverPosition(connectionPair);
                const popoverElement = this._overlayRef!.overlayElement.querySelector<HTMLElement>('.sb-popover');
                if (!popoverElement) {
                    return;
                }

                this.arrowStyles = this.GetArrowStyles(connectionPair, this.target!.getBoundingClientRect(), popoverElement.getBoundingClientRect());
            });

            this._overlayRef.backdropClick().subscribe(() => this.Close());

            const portal = new TemplatePortal(this.popoverTemplate, this._ViewContainer);
            this._overlayRef.attach(portal);
            this._overlayRef?.updatePosition();
            
        });
    }

    private GetPositions(): ConnectedPosition[] {
        const order: PopoverPosition[] = [
            PopoverPosition.TopLeft,
            PopoverPosition.TopRight,
            PopoverPosition.Right,
            PopoverPosition.RightTop,
            PopoverPosition.RightBottom,
            PopoverPosition.Left,
            PopoverPosition.LeftTop,
            PopoverPosition.LeftBottom,
            ...(this.position ? [this.position] : []),
            PopoverPosition.Bottom,
            PopoverPosition.BottomLeft,
            PopoverPosition.BottomRight,
            PopoverPosition.Top,
        ];

        return [...new Set(order)].flatMap(position => this.GetPosition(position));
    }

    private GetPosition(position: PopoverPosition): ConnectedPosition[] {
        switch (position) {
            case PopoverPosition.TopLeft:
                return [
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                        offsetY: -10,
                    },
                ];
            case PopoverPosition.Top:
                return [
                    {
                        originX: 'center',
                        originY: 'top',
                        overlayX: 'center',
                        overlayY: 'bottom',
                        offsetY: -10,
                    },
                ];
            case PopoverPosition.TopRight:
                return [
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetY: -10,
                        offsetX: 4
                    },
                ];
            case PopoverPosition.BottomLeft:
                return [
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetY: 10,
                    },
                ];
            case PopoverPosition.Bottom:
                return [
                    {
                        originX: 'center',
                        originY: 'bottom',
                        overlayX: 'center',
                        overlayY: 'top',
                        offsetY: 10,
                    },
                ];
            case PopoverPosition.BottomRight:
                return [
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetY: 10,
                        offsetX: -6
                    },
                ];
            case PopoverPosition.LeftTop:
                return [
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetX: -10,
                    },
                ];
            case PopoverPosition.Left:
                return [
                    {
                        originX: 'start',
                        originY: 'center',
                        overlayX: 'end',
                        overlayY: 'center',
                        offsetX: -10,
                    },
                ];
            case PopoverPosition.LeftBottom:
                return [
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetX: -10,
                    },
                ];
            case PopoverPosition.RightTop:
                return [
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetX: -6,
                    },
                ];
            case PopoverPosition.Right:
                return [
                    {
                        originX: 'end',
                        originY: 'center',
                        overlayX: 'start',
                        overlayY: 'center',
                        offsetX: -6,
                    },
                ];
            case PopoverPosition.RightBottom:
                return [
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'bottom',
                        offsetX: -6,
                    },
                ];
            default:
                return [
                    {
                        originX: 'center',
                        originY: 'bottom',
                        overlayX: 'center',
                        overlayY: 'top',
                        offsetY: 10,
                    },
                ];
        }
    }

    private GetPopoverPosition(position: ConnectedPosition): PopoverPosition {
        if (position.originY === 'top' && position.overlayY === 'bottom') {
            return position.originX === 'start'
                ? PopoverPosition.TopLeft
                : position.originX === 'end'
                    ? PopoverPosition.TopRight
                    : PopoverPosition.Top;
        }

        if (position.originY === 'bottom' && position.overlayY === 'top') {
            return position.originX === 'start'
                ? PopoverPosition.BottomLeft
                : position.originX === 'end'
                    ? PopoverPosition.BottomRight
                    : PopoverPosition.Bottom;
        }

        if (position.originX === 'start' && position.overlayX === 'end') {
            return position.originY === 'top'
                ? PopoverPosition.LeftTop
                : position.originY === 'bottom'
                    ? PopoverPosition.LeftBottom
                    : PopoverPosition.Left;
        }

        if (position.originX === 'end' && position.overlayX === 'start') {
            return position.originY === 'top'
                ? PopoverPosition.RightTop
                : position.originY === 'bottom'
                    ? PopoverPosition.RightBottom
                    : PopoverPosition.Right;
        }

        return PopoverPosition.Bottom;
    }

    private GetArrowStyles(position: ConnectedPosition, targetRect: DOMRect, popoverRect: DOMRect): Record<string, string> {
        if (position.originY !== position.overlayY) {
            const offset = targetRect.left + targetRect.width / 2 - popoverRect.left - 9 + 2;
            return { '--sb-popover-arrow-offset-x': `${offset}px` };
        }

        const offset = targetRect.top + targetRect.height / 2 - popoverRect.top - 7.25;
        return { '--sb-popover-arrow-offset-y': `${offset}px` };
    }

    @HostListener('document:click', ['$event'])
    Close() {
        this._positionChangeSubscription?.unsubscribe();
        this._positionChangeSubscription = undefined;

        if (this._overlayRef && this._overlayRef.hasAttached()) {
            this._overlayRef.dispose();
            this._overlayRef = undefined;
        }
    }

    @ViewChild('popoverTemplate', { static: false }) popoverTemplate!: TemplateRef<any>;
}
